import {
  IDLE_ACTIVITY_UPDATE_KEY,
  IDLE_LOGOFF_LIVE_WINDOW_AFTER_S,
  IDLE_LOGOFF_LIVE_WINDOW_BEFORE_S,
  IDLE_LOGOFF_USER_TYPES,
  IDLE_LOGOFF_USER_TYPES_ENV,
  IDLE_LOGOFF_S_BY_USER_TYPE,
} from "@/lib/modules/auth/idle-logoff/constants";
import type {
  IdleLogoffConfig,
  IdleLogoffSessionState,
  IdleLogoffTokenFields,
} from "@/lib/modules/auth/idle-logoff/types";
import type { UserType } from "@prisma/client";

/** Which user types the logoff is on for. */
export const getIdleLogoffConfig = (
  env: Record<string, string | undefined> = process.env,
): IdleLogoffConfig => {
  const enabled = (env[IDLE_LOGOFF_USER_TYPES_ENV] ?? "")
    .split(",")
    .map((value) => value.trim());
  return {
    userTypes: IDLE_LOGOFF_USER_TYPES.filter((userType) =>
      enabled.includes(userType),
    ),
  };
};

/**
 * The idle limit for this token in seconds, or null when the logoff doesn't
 * apply. An impersonating admin is judged as the admin at the keyboard, not
 * the user they're viewing as.
 */
export const getIdleLogoffLimitS = (
  token: IdleLogoffTokenFields,
  config: IdleLogoffConfig,
): number | null => {
  const userType = _getSignedInUserType(token);
  return userType && config.userTypes.includes(userType)
    ? (IDLE_LOGOFF_S_BY_USER_TYPE[userType] ?? null)
    : null;
};

/** True once the token has gone unused past its idle limit. */
export const isIdleLoggedOut = (
  token: IdleLogoffTokenFields | null | undefined,
  config: IdleLogoffConfig,
  nowS: number,
): boolean => {
  if (!token) {
    return false;
  }
  if (token.idleLoggedOut === true) {
    return true;
  }
  const limitS = getIdleLogoffLimitS(token, config);
  return (
    limitS !== null &&
    token.lastActiveAt !== undefined &&
    nowS - token.lastActiveAt >= limitS
  );
};

/**
 * Brings the token's idle state up to date, in place. Real activity moves
 * lastActiveAt to now unless the token is already past its limit, so a late
 * report can't revive it. A token the logoff doesn't apply to carries no
 * timestamp, so turning the flag on later starts everyone's clock fresh.
 * Returns true when this call is the one that logs the session out.
 */
export const updateIdleLogoffToken = (
  token: IdleLogoffTokenFields,
  {
    isActivity,
    config,
    nowS,
  }: { isActivity: boolean; config: IdleLogoffConfig; nowS: number },
): boolean => {
  if (token.idleLoggedOut === true) {
    return false;
  }
  if (getIdleLogoffLimitS(token, config) === null) {
    delete token.lastActiveAt;
    return false;
  }
  if (isIdleLoggedOut(token, config, nowS)) {
    return true;
  }
  if (isActivity || token.lastActiveAt === undefined) {
    token.lastActiveAt = nowS;
  }
  return false;
};

/** The client's view of the logoff, or null when it doesn't apply. */
export const getIdleLogoffSessionState = (
  token: IdleLogoffTokenFields,
  config: IdleLogoffConfig,
  nowS: number,
): IdleLogoffSessionState | null => {
  const limitS = getIdleLogoffLimitS(token, config);
  if (limitS === null || token.lastActiveAt === undefined) {
    return null;
  }
  return {
    timeoutS: limitS,
    expiresInS: Math.max(0, token.lastActiveAt + limitS - nowS),
  };
};

/** Whether a useSession().update() payload is a report of real input. */
export const isIdleActivityReport = (payload: unknown): boolean =>
  typeof payload === "object" &&
  payload !== null &&
  (payload as Record<string, unknown>)[IDLE_ACTIVITY_UPDATE_KEY] === true;

/** Whether an appointment is close enough to now to be in session. */
export const isInLiveAppointmentWindow = (
  { startTime, endTime }: { startTime: Date; endTime: Date },
  nowMs: number,
): boolean =>
  nowMs >= startTime.getTime() - IDLE_LOGOFF_LIVE_WINDOW_BEFORE_S * 1000 &&
  nowMs <= endTime.getTime() + IDLE_LOGOFF_LIVE_WINDOW_AFTER_S * 1000;

/** The `idle_logoff` log line's fields. Never an email or phone number. */
export const getIdleLogoffLogFields = (token: IdleLogoffTokenFields) => ({
  action: "idle_logoff",
  userId: token.originalUserId ?? token.userId,
  userType: _getSignedInUserType(token),
  ...(token.originalUserId !== undefined && { isImpersonating: true }),
});

const _getSignedInUserType = (
  token: IdleLogoffTokenFields,
): UserType | undefined =>
  token._preImpersonationToken?.userType ?? token.userType;
